import React, { useState, useEffect } from 'react';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';

const ReportSortingTest = () => {
  const { 
    fetchAttendance, 
    exportToPDF, 
    exportToCSV,
    attendanceList,
    pagination,
    isLoading 
  } = useAttendance();
  
  const { user, isAuthenticated } = useAuth();
  const [testResults, setTestResults] = useState([]);
  const [isRunningTests, setIsRunningTests] = useState(false);

  const runSortingTests = async () => {
    setIsRunningTests(true);
    setTestResults([]);
    
    if (!isAuthenticated) {
      setTestResults([{
        name: 'Authentication Check',
        passed: false,
        message: 'User not authenticated. Please log in first.',
        timestamp: new Date().toISOString()
      }]);
      setIsRunningTests(false);
      toast.error('Please log in to run tests');
      return;
    }
    
    const tests = [
      {
        name: 'Test 1: Backend API Sorting (July 2025)',
        test: async () => {
          try {
            const startDate = '2025-07-01';
            const endDate = '2025-07-31';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 100
            });
            
            const data = result.attendance || [];
            
            if (data.length < 2) {
              return {
                passed: false,
                message: 'Not enough data to test sorting',
                data: { recordCount: data.length }
              };
            }
            
            // Check if data is sorted in ascending order (oldest first)
            const isSorted = data.every((record, index) => {
              if (index === 0) return true;
              const currentDate = new Date(record.stepIn);
              const previousDate = new Date(data[index - 1].stepIn);
              return currentDate >= previousDate;
            });
            
            const firstDate = new Date(data[0].stepIn).toISOString().split('T')[0];
            const lastDate = new Date(data[data.length - 1].stepIn).toISOString().split('T')[0];
            
            return {
              passed: isSorted,
              message: `Sorting: ${isSorted ? 'CORRECT' : 'INCORRECT'} - First: ${firstDate}, Last: ${lastDate}`,
              data: { 
                recordCount: data.length,
                isSorted,
                firstDate,
                lastDate,
                sampleDates: data.slice(0, 5).map(r => new Date(r.stepIn).toISOString().split('T')[0])
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 2: PDF Export Sorting (August 2025)',
        test: async () => {
          try {
            const startDate = '2025-08-01';
            const endDate = '2025-08-31';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 50
            });
            
            const data = result.attendance || [];
            
            if (data.length < 2) {
              return {
                passed: false,
                message: 'Not enough data to test sorting',
                data: { recordCount: data.length }
              };
            }
            
            // Test PDF export with sorting
            const startTime = Date.now();
            await exportToPDF(data, `test_sorting_august_${Date.now()}.pdf`);
            const endTime = Date.now();
            
            // Check if data is sorted in ascending order
            const isSorted = data.every((record, index) => {
              if (index === 0) return true;
              const currentDate = new Date(record.stepIn);
              const previousDate = new Date(data[index - 1].stepIn);
              return currentDate >= previousDate;
            });
            
            const firstDate = new Date(data[0].stepIn).toISOString().split('T')[0];
            const lastDate = new Date(data[data.length - 1].stepIn).toISOString().split('T')[0];
            
            return {
              passed: isSorted,
              message: `PDF Export Sorting: ${isSorted ? 'CORRECT' : 'INCORRECT'} - First: ${firstDate}, Last: ${lastDate} (${endTime - startTime}ms)`,
              data: { 
                recordCount: data.length,
                isSorted,
                firstDate,
                lastDate,
                exportTime: endTime - startTime
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `PDF export failed: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 3: CSV Export Sorting (September 2025)',
        test: async () => {
          try {
            const startDate = '2025-09-01';
            const endDate = '2025-09-30';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 50
            });
            
            const data = result.attendance || [];
            
            if (data.length < 2) {
              return {
                passed: false,
                message: 'Not enough data to test sorting',
                data: { recordCount: data.length }
              };
            }
            
            // Test CSV export with sorting
            const startTime = Date.now();
            await exportToCSV(data, `test_sorting_september_${Date.now()}.csv`);
            const endTime = Date.now();
            
            // Check if data is sorted in ascending order
            const isSorted = data.every((record, index) => {
              if (index === 0) return true;
              const currentDate = new Date(record.stepIn);
              const previousDate = new Date(data[index - 1].stepIn);
              return currentDate >= previousDate;
            });
            
            const firstDate = new Date(data[0].stepIn).toISOString().split('T')[0];
            const lastDate = new Date(data[data.length - 1].stepIn).toISOString().split('T')[0];
            
            return {
              passed: isSorted,
              message: `CSV Export Sorting: ${isSorted ? 'CORRECT' : 'INCORRECT'} - First: ${firstDate}, Last: ${lastDate} (${endTime - startTime}ms)`,
              data: { 
                recordCount: data.length,
                isSorted,
                firstDate,
                lastDate,
                exportTime: endTime - startTime
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `CSV export failed: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 4: Large Dataset Sorting (All July 2025)',
        test: async () => {
          try {
            const startDate = '2025-07-01';
            const endDate = '2025-07-31';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 1000
            });
            
            const data = result.attendance || [];
            
            if (data.length < 10) {
              return {
                passed: false,
                message: 'Not enough data to test large dataset sorting',
                data: { recordCount: data.length }
              };
            }
            
            // Check if data is sorted in ascending order
            const isSorted = data.every((record, index) => {
              if (index === 0) return true;
              const currentDate = new Date(record.stepIn);
              const previousDate = new Date(data[index - 1].stepIn);
              return currentDate >= previousDate;
            });
            
            const firstDate = new Date(data[0].stepIn).toISOString().split('T')[0];
            const lastDate = new Date(data[data.length - 1].stepIn).toISOString().split('T')[0];
            
            return {
              passed: isSorted,
              message: `Large Dataset Sorting: ${isSorted ? 'CORRECT' : 'INCORRECT'} - First: ${firstDate}, Last: ${lastDate} (${data.length} records)`,
              data: { 
                recordCount: data.length,
                isSorted,
                firstDate,
                lastDate,
                sampleDates: data.slice(0, 10).map(r => new Date(r.stepIn).toISOString().split('T')[0])
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      }
    ];

    const results = [];
    
    for (const test of tests) {
      try {
        console.log(`🧪 Running: ${test.name}`);
        const result = await test.test();
        results.push({
          ...result,
          name: test.name,
          timestamp: new Date().toISOString()
        });
        
        console.log(`✅ ${test.name}: ${result.message}`);
      } catch (error) {
        console.error(`❌ ${test.name}: ${error.message}`);
        results.push({
          name: test.name,
          passed: false,
          message: `Error: ${error.message}`,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    setTestResults(results);
    setIsRunningTests(false);
    
    const passedTests = results.filter(r => r.passed).length;
    const totalTests = results.length;
    
    if (passedTests === totalTests) {
      toast.success(`All ${totalTests} sorting tests passed! 🎉`);
    } else {
      toast.error(`${passedTests}/${totalTests} tests passed`);
    }
  };

  return (
    <div className="card p-6">
      <h2 className="text-2xl font-bold mb-4">📊 Report Sorting Test Suite</h2>
      
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-800 mb-2">🎯 Testing Report Data Sorting:</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Backend API: Should return data in ascending order (oldest first)</li>
          <li>• PDF Export: Should sort data chronologically</li>
          <li>• CSV Export: Should sort data chronologically</li>
          <li>• Large Datasets: Should maintain proper sorting</li>
        </ul>
      </div>
      
      <div className="mb-6">
        <button
          onClick={runSortingTests}
          disabled={isRunningTests}
          className="btn btn-primary"
        >
          {isRunningTests ? 'Running Tests...' : 'Run Sorting Tests'}
        </button>
      </div>

      {/* Current Data Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <h3 className="font-semibold">Authentication</h3>
          <p className="text-sm text-gray-600">
            Status: {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
          </p>
          <p className="text-sm text-gray-600">
            User: {user?.name || 'N/A'}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Current Data</h3>
          <p className="text-sm text-gray-600">
            Records: {attendanceList.length}
          </p>
          <p className="text-sm text-gray-600">
            Total: {pagination.totalRecords}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Pagination</h3>
          <p className="text-sm text-gray-600">
            Page: {pagination.currentPage}/{pagination.totalPages}
          </p>
          <p className="text-sm text-gray-600">
            Limit: {pagination.limit}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Status</h3>
          <p className="text-sm text-gray-600">
            Loading: {isLoading ? 'Yes' : 'No'}
          </p>
          <p className="text-sm text-gray-600">
            Tests: {testResults.length}
          </p>
        </div>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Test Results</h3>
          {testResults.map((result, index) => (
            <div key={index} className={`card p-4 ${result.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">{result.name}</h4>
                  <p className="text-sm text-gray-600">{result.message}</p>
                  {result.data && (
                    <div className="mt-2 text-xs text-gray-500">
                      <pre>{JSON.stringify(result.data, null, 2)}</pre>
                    </div>
                  )}
                </div>
                <div className={`badge ${result.passed ? 'badge-success' : 'badge-error'}`}>
                  {result.passed ? 'PASS' : 'FAIL'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportSortingTest;

